import { schemas } from '@repo/contracts'
import { apiErrorHandler } from '../errorHandler'
import { parseDataOfType } from '../../common/parseDataType'
import type { z } from 'zod'

// -- UI enum value map (kept hand-written; ContentReferences references e.g. RagDocType.Question).
// The generated z.enum(...) inside apihttp_GetRefsDetailsV2Response governs wire parsing; this map
// is a UI convenience and stays type-compatible (same string-literal union). --

export const RagDocType = {
  Unknown: 'unknown',
  Text: 'text',
  Question: 'question',
  Answer: 'answer',
  File: 'file',
  ConnectorFile: 'connector_file',
  ConnectorWebCrawler: 'connector_webcrawler'
} as const

export type RagDocType = (typeof RagDocType)[keyof typeof RagDocType]

// -- Wire type (generated; @repo/contracts is the source of truth) --

export type ReferenceDetails = z.infer<typeof schemas.apihttp_GetRefsDetailsV2Response>
export type SourceSummary = z.infer<typeof schemas.apihttp_GetSourceSummaryResponse>

export class SourceSummariesUnavailableError extends Error {
  constructor() {
    super('Some source details are no longer available')
    this.name = 'SourceSummariesUnavailableError'
  }
}

export async function fetchRefsDetails(docIds: Array<number>): Promise<Array<ReferenceDetails>> {
  if (docIds.length === 0) {
    return []
  }

  const res = await fetch('/api/content/refs/v2', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify({ docIds })
  })
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_GetRefsDetailsV2Response.array())
}

export async function fetchSourceSummaries(docIds: Array<number>): Promise<Array<SourceSummary>> {
  if (docIds.length === 0) {
    return []
  }

  const request = parseDataOfType({ docIds }, schemas.apihttp_GetSourceSummariesRequest)
  const res = await fetch('/api/content/refs/summary', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'same-origin',
    body: JSON.stringify(request)
  })
  if (res.status === 404) {
    await res.body?.cancel()
    throw new SourceSummariesUnavailableError()
  }
  await apiErrorHandler(res)
  const data: unknown = await res.json()
  return parseDataOfType(data, schemas.apihttp_GetSourceSummaryResponse.array())
}
